import React, { useState, useEffect } from 'react';
import roleService from '../services/roleService';
import { useAuth } from '../context/AuthContext';
import IconButton from '../components/IconButton';

export default function Roles() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingRole, setEditingRole] = useState(null);
  const [form, setForm] = useState({ name: '', permissions: {} });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const { isAdmin } = useAuth();

  useEffect(() => {
    loadRoles();
  }, []);

  const loadRoles = async () => {
    try {
      const { data } = await roleService.list();
      setRoles(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const modules = ['Client', 'Task', 'User', 'Service'];
  const actions = ['create', 'read', 'update', 'delete'];

  const startEdit = (role) => {
    // Parse permissions if it's a string
    let perms = role.permissions || {};
    if (typeof perms === 'string') {
      try {
        perms = JSON.parse(perms);
      } catch (e) {
        console.error(`Failed to parse permissions for role ${role.name}:`, e);
        perms = {};
      }
    }
    
    // Deep clone permissions so edits don't mutate list until saved
    setEditingRole(role);
    setForm({ name: role.name, permissions: JSON.parse(JSON.stringify(perms)) });
    setModalError('');
  };

  const handleToggle = (moduleName, action) => {
    setForm((f) => {
      const current = f.permissions || {};
      const modulePerm = current[moduleName] || { create: false, read: false, update: false, delete: false };
      const updatedModulePerm = { ...modulePerm, [action]: !modulePerm[action] };
      const updatedPermissions = { ...current, [moduleName]: updatedModulePerm };
      return { ...f, permissions: updatedPermissions };
    });
  };

  const saveRole = async () => {
    setSaving(true);
    setModalError('');
    try {
      if (editingRole && editingRole.id) {
        await roleService.update(editingRole.id, form);
      } else {
        await roleService.create(form);
      }
      await loadRoles();
      cancelEdit();
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to save role';
      setModalError(msg);
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditingRole(null);
    setForm({ name: '', permissions: {} });
  };

  const deleteRole = async (id, roleName) => {
    if (roleName && roleName.toLowerCase() === 'admin') {
      alert('Cannot delete the built-in admin role');
      return;
    }
    if (!window.confirm('Delete this role?')) return;
    try {
      setError('');
      await roleService.remove(id);
      alert('Role deleted successfully');
      await loadRoles();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete role');
    }
  };

  if (!isAdmin) return <div>Access denied</div>;
  if (loading) return <div>Loading...</div>;

  return (
    <div className="roles-page">
      <div className="header">
        <h2>Roles & Permissions</h2>
        <div className="actions-right">
          <button
            className="add-button add-action-button"
            onClick={() => startEdit({ name: '', permissions: {} })}
            aria-label="Add role"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ marginRight: 8 }}>
              <path d="M8 1V15M1 8H15" stroke="currentColor" strokeWidth="2"/>
            </svg>
            Add Role
          </button>
        </div>
      </div>

      {error && <div style={{ color: 'red' }}>{error}</div>}

      <div className="table-responsive">
      <table className="roles-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th>Role</th>
            {modules.map(m => (
              <th key={m}>{m}</th>
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {roles.map(role => {
            // Ensure permissions is an object, parse if string
            let perms = role.permissions || {};
            if (typeof perms === 'string') {
              try {
                perms = JSON.parse(perms);
              } catch (e) {
                console.error(`Failed to parse permissions for role ${role.name}:`, e);
                perms = {};
              }
            }
            
            return (
            <tr key={role.id} style={{ borderTop: '1px solid #eee' }}>
              <td>{role.name}</td>
              {modules.map(m => (
                <td key={m} style={{ padding: 8 }}>
                  {actions.map(a => {
                    const isOn = !!(perms && perms[m] && perms[m][a]);
                    return (
                      <label key={a} className={`perm ${isOn ? 'on' : 'off'}`} style={{ marginRight: 8 }}>
                        <input type="checkbox" checked={isOn} readOnly disabled />
                        <span style={{ fontSize: 12 }}>{a[0].toUpperCase()}</span>
                      </label>
                    );
                  })}
                </td>
              ))}
              <td>
                <div style={{ display: 'flex', gap: 8 }}>
                  <IconButton title="Edit role" onClick={() => startEdit(role)} ariaLabel={`Edit ${role.name}`} className="edit-btn" size="small">
                    <svg viewBox="0 0 24 24" fill="none"><path d="M3 21v-3.6l11-11L20.6 9 9.6 20H3z" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </IconButton>

                  <IconButton title="Delete role" onClick={() => deleteRole(role.id, role.name)} ariaLabel={`Delete ${role.name}`} className="delete-btn" size="small">
                    <svg viewBox="0 0 24 24" fill="none"><path d="M3 6h18" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/><path d="M8 6v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </IconButton>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {editingRole !== null && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{editingRole.id ? 'Edit Role' : 'Create Role'}</h3>
              <button className="close-button" onClick={cancelEdit}>&times;</button>
            </div>

            <form onSubmit={async (e) => { e.preventDefault(); await saveRole(); }} className="role-form">
              <div className="form-group">
                <label>Role name:</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
              </div>

              {modalError && <div style={{ color: '#de350b', marginTop: 8 }}>{modalError}</div>}

              <div className="permissions-grid">
                {modules.map(m => (
                  <div key={m} className="module-row">
                    <strong>{m}</strong>
                    <div className="actions">
                      {actions.map(a => {
                        const id = `perm-${m}-${a}`;
                        const isChecked = !!(form.permissions[m] && form.permissions[m][a]);
                        return (
                          <label key={a} htmlFor={id} className={`perm ${isChecked ? 'on' : 'off'}`}>
                            <input id={id} name={id} type="checkbox" checked={isChecked} onChange={() => handleToggle(m, a)} />
                            <span className="perm-label">{a}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="modal-actions">
                <button type="submit" className="save-btn" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                <button type="button" onClick={cancelEdit} className="cancel-btn" disabled={saving}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style jsx>{`
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0,0,0,0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
        }
        .modal {
          background: white;
          padding: 20px;
          border-radius: 8px;
          width: 90%;
          max-width: 600px;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }
        .close-button {
          background: none;
          border: none;
          font-size: 24px;
          cursor: pointer;
          color: #42526e;
        }
        .permissions-grid { display: grid; gap: 12px; }
        .module-row { display: flex; align-items: center; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f1f1f1; }
        .actions { display: flex; gap: 8px; }
        .perm { display: inline-flex; align-items: center; gap:6px; padding: 6px 8px; border-radius: 6px; cursor: pointer; border: 1px solid transparent; }
        .perm input { margin: 0; }
        .perm.on { background: #e6ffed; border-color: #b3f5c4; }
        .perm.off { background: #f7f7f7; color: #65748b; }
        .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
        .actions-right { display: flex; gap: 12px; }
        .add-button { background-color: #00875a; color: white; padding: 8px 12px; border: none; border-radius: 4px; cursor: pointer; font-weight: 500; display: inline-flex; align-items: center; }
        .roles-table th, .roles-table td { padding: 8px 12px; text-align: left; }
        .icon-btn { border-radius: 6px; }
        .edit-btn { background: #0f66ff; color: white; }
        .edit-btn svg { stroke: white; }
        .delete-btn { background: #de350b; color: white; }
        .delete-btn svg { stroke: white; }
        .modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 12px; }
        .save-btn { background: #00875a; color: white; padding: 8px 12px; border: none; border-radius: 4px; }
        .cancel-btn { background: #eee; padding: 8px 12px; border: none; border-radius: 4px; }
      `}</style>
    </div>
  );
}
